// A linha do Catalogo do fornecedor que a NOTA DE ENTRADA grava — embalagem, quantidade e o preco
// DA EMBALAGEM. Pedido dele, 29/10/2026, com a torneira 32582 da NOVA CASA na mao: o XML fatura
// 360 UN a R$ 1,241, mas ele compra o pacote de 12; a linha do catalogo nascia UN / 1 / 1,241 e a
// cotacao, que desde a [DA-864] conta EMBALAGENS do fornecedor, lia "1 un" onde era um pacote.
// *"A Tramontina envia o disjuntor no xml por unidade, mas a cotacao e por caixa [...] Este padrao
// se repete em varios fornecedores"*; e *"Alguns produtos sao vendidos por unidade, mas quase
// sempre e caixa ou pacote"* — o avulso e' a excecao, corrigida na tela de importacao.
//
// ⛔ A "Qtd na emb." da linha e' o que vem DENTRO da embalagem, na unidade do conteudo — regra
// dele, mesmo dia, com o parafuso 1960 (CX100) na tela: *"Deveria ter sido informado 100 no lugar
// de 1 [...] 1 e a quantidade de caixa, mas nao e a quantidade dentro da caixa"*. Ela NAO conta
// unidades do estoque: o estoque do 1960 conta caixas. A primeira versao desta peca (v.17/.18)
// multiplicava o preco pela Qtd da linha, e a CX 200 do parafuso 1831 sairia R$ 3.726 na proxima
// nota (62 linhas do catalogo estavam nesse caso; nenhuma nota entrou no intervalo).
//
// Mora aqui porque a TELA mostra a previa e o SERVIDOR grava: duas copias divergiriam.

function sigla(v: string | null | undefined): string {
  return (v ?? '').trim().toUpperCase();
}

function numero(v: number | string | null | undefined): number {
  const n = Number(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

/** O que o cadastro do produto diz sobre embalagem. */
export interface CadastroEmbalagem {
  /** produtos.unidade_compra */
  unidadeCompra: string | null | undefined;
  /** produtos.qtd_embalagem_padrao */
  qtdEmbCadastro: number | string | null | undefined;
  /** A sigla da unidade de VENDA — a que o estoque conta. */
  unidadeVenda: string | null | undefined;
  /** produtos.unidade_conteudo_embalagem (MT, UN...); vazia = a de venda. */
  unidadeConteudo: string | null | undefined;
}

/**
 * A Qtd na Emb do cadastro conta UNIDADES DO ESTOQUE dentro da embalagem de compra?
 *
 * Medido em 29/10/2026:
 *  - vende UN, compra CX 12 (disjuntor, torneira PCT 12): sim, 1 caixa = 12 do estoque;
 *  - vende RL, compra RL 100 (FLEX 1,5MM) ou CX/CX 200 (parafuso 1831): NAO — o 100 e' o que vem
 *    DENTRO do rolo vendido inteiro; o estoque conta rolos;
 *  - vende UN com conteudo MT, compra RL 25 (corrugada): NAO — o 25 sao metros do rolo.
 */
export function cadastroContaUnidadesDoEstoque(e: CadastroEmbalagem): boolean {
  const compra = sigla(e.unidadeCompra);
  const venda = sigla(e.unidadeVenda);
  const conteudo = sigla(e.unidadeConteudo);
  if (!compra || !venda || !(numero(e.qtdEmbCadastro) > 1)) return false;
  if (compra === venda) return false;
  return !conteudo || conteudo === venda;
}

/**
 * Quantas unidades do CONTEUDO cabem em 1 unidade do estoque. 1 quando o estoque ja conta o
 * conteudo (torneira) ou quando o cadastro nao diz (parafuso 1960, CX com Qtd 1).
 */
export function conteudoPorUnidadeDoEstoque(e: CadastroEmbalagem): number {
  const qc = numero(e.qtdEmbCadastro);
  if (!(qc > 1) || cadastroContaUnidadesDoEstoque(e)) return 1;
  const venda = sigla(e.unidadeVenda);
  const conteudo = sigla(e.unidadeConteudo);
  const estoqueEEmbalagem = (!!venda && sigla(e.unidadeCompra) === venda) || (!!conteudo && conteudo !== venda);
  return estoqueEEmbalagem ? qc : 1;
}

/**
 * Quantas unidades do ESTOQUE vale 1 embalagem desta linha do catalogo — o numero que converte o
 * preco da nota (que e' por unidade do estoque, depois do Qt.Emb do item) no preco da embalagem.
 *
 *  - a embalagem da linha E' a unidade do estoque (CX do parafuso, RL do FLEX): 1, seja qual for a
 *    Qtd de dentro — a caixa de 100 parafusos e' 1 caixa no estoque;
 *  - senao, a Qtd da linha (conteudo) dividida pelo conteudo de 1 unidade do estoque: PCT 12 da
 *    torneira = 12 do estoque; RL 25 metros da corrugada vendida em rolo de 25 = 1.
 */
export function estoqueNaEmbalagemDaLinha(
  linha: {
    embalagem: string | null | undefined;
    quantidade: number | string | null | undefined;
    /** O preco que a linha JA tem — so pra desempatar a mesma sigla com dois tamanhos (ver abaixo).
     *  Ausente quando o operador mexeu na tela: ai a Qtd dele manda. */
    precoAtual?: number | string | null;
  },
  cad: CadastroEmbalagem,
  nota?: {
    embalagemXml: string | null | undefined;
    qtdEmbItem: number | string | null | undefined;
    /** O preco do XML (da unidade comercial dele), pro desempate. */
    precoXml?: number | string | null;
  },
): number {
  const venda = sigla(cad.unidadeVenda);
  const emb = sigla(linha.embalagem);
  const q = numero(linha.quantidade) > 0 ? numero(linha.quantidade) : 1;
  const c = conteudoPorUnidadeDoEstoque(cad);
  const estoqueContaEmbalagens = (!!venda && sigla(cad.unidadeCompra) === venda) || c > 1;
  // A linha na MESMA unidade da nota: 1 embalagem da linha = 1 unidade do XML, e o Qt.Emb do item
  // ja diz quanto ela vale no estoque. E' a resposta que nao depende do cadastro — o cano de cobre
  // (XML em KG, Qt.Emb 3 metros, estoque em metro, linha "KG / 1") so se acerta por aqui.
  // ⚠️ Mas a mesma SIGLA pode ser outro TAMANHO: o FLEX 10mm da MARTINS tem a linha "CD / 100" e a
  // nota veio num CD de 500 m (censo de 29/10). Com o estoque contando o conteudo e a linha dizendo
  // quanto tem dentro (Qtd > 1), vale a Qtd da linha — 100 m pelo preco de 100 m.
  if (nota && emb && emb === sigla(nota.embalagemXml)) {
    const qi = numero(nota.qtdEmbItem) > 0 ? numero(nota.qtdEmbItem) : 1;
    const peloXml = qi;
    const pelaLinha = q / c;
    if (peloXml === pelaLinha) return peloXml;
    // DESEMPATE pelo preco que a linha ja tem. O cadastro nao separa "CD / 100" do FLEX 10mm
    // (estoque em METRO: a linha e' 100 m) do "CD / 100" do FLEX 1,5mm (estoque em ROLO: a linha e'
    // 1 rolo) — as duas tem a mesma forma, e cada regra fixa errava uma das duas por 100x no censo
    // de 29/10. Os dois candidatos diferem pelo tamanho da embalagem (5x, 100x...), muito alem de
    // qualquer reajuste, entao o mais perto do preco atual (em escala de vezes) e' o tamanho certo.
    const atual = numero(linha.precoAtual);
    const xml = numero(nota.precoXml);
    if (atual > 0 && xml > 0) {
      const porEstoque = xml / qi;
      const dist = (s: number) => Math.abs(Math.log((porEstoque * s) / atual));
      return dist(pelaLinha) < dist(peloXml) ? pelaLinha : peloXml;
    }
    if (estoqueContaEmbalagens || q === 1) return peloXml;
    return pelaLinha;
  }
  // A embalagem da linha E' a unidade do estoque e o estoque conta EMBALAGENS (o parafuso vende a
  // CX; o FLEX, o RL): 1, seja qual for a Qtd de dentro. Quando o estoque conta a unidade solta, a
  // mesma sigla com Qtd maior e' um pacote com nome errado — a fita TAMBASA 0344 "UN / 5" e' o
  // pacote de 5 (o censo de 29/10 a dividiria por 5) — e cai na conta do conteudo, logo abaixo.
  if (venda && emb === venda && estoqueContaEmbalagens) return 1;
  // "RL / 1" numa corrugada (estoque em rolo, conteudo em metro) e' a linha antiga, gravada antes
  // da regra do conteudo: 1 rolo, nao 1 metro. So a embalagem NA unidade do conteudo (MT / 1) e'
  // um metro de verdade. Sem isto o preco do rolo cairia 25x na proxima nota (2 linhas, 29/10).
  const conteudo = sigla(cad.unidadeConteudo) || venda;
  if (c > 1 && q === 1 && emb !== conteudo) return 1;
  return q / c;
}

export interface EmbalagemPelaNotaEntrada extends CadastroEmbalagem {
  /** A unidade comercial do XML (uCom). */
  embalagemXml: string | null | undefined;
  /** O Qt.Emb da linha do item: quantas unidades do estoque vem em 1 unidade do XML. */
  qtdEmbItem: number | string | null | undefined;
}

/**
 * Com que embalagem uma linha NOVA do catalogo nasce a partir da nota. A quantidade sai sempre no
 * CONTEUDO (o que vem dentro).
 *
 *  - XML na unidade do estoque e o cadastro compra numa embalagem com varias delas (torneira UN,
 *    compra PCT 12): nasce na embalagem do CADASTRO, PCT / 12;
 *  - senao, a embalagem da nota, com a quantidade de dentro: Qt.Emb do item x conteudo de 1
 *    unidade do estoque (o parafuso 1831 CX chega CX / 200; o disjuntor em CX 12 chega CX / 12).
 *
 * ⚠️ So pra linha que ainda NAO tem quantidade: linha existente guarda a embalagem dela (quem
 * muda e' o operador, na tela), senao o avulso corrigido a mao voltaria pro pacote a cada nota.
 */
export function embalagemDaLinhaNovaPelaNota(e: EmbalagemPelaNotaEntrada): { embalagem: string; quantidade: number; doCadastro: boolean } {
  const embXml = sigla(e.embalagemXml) || 'UN';
  const qtdItem = numero(e.qtdEmbItem) > 0 ? numero(e.qtdEmbItem) : 1;
  const compra = sigla(e.unidadeCompra);
  if (qtdItem === 1 && compra !== embXml && cadastroContaUnidadesDoEstoque(e)) {
    return { embalagem: compra, quantidade: numero(e.qtdEmbCadastro), doCadastro: true };
  }
  return { embalagem: embXml, quantidade: qtdItem * conteudoPorUnidadeDoEstoque(e), doCadastro: false };
}

/**
 * A linha do catalogo em UNIDADE (Qtd 1, ou sem embalagem) adota uma embalagem MAIOR que a casa ja
 * conhece — regra dele, 30/10/2026, com a nota 5703 da BETEL: o conector 0656 tinha "PCT / 10" no
 * cadastro e a linha seguiu "UN / 1"; o terminal 1963 estava "PCT / 10" no CNPJ vizinho e o outro
 * CNPJ nasceu "UN / 1". *"Os 2 cnpj sempre usam a mesma embalagem."*
 *
 * Ordem: o CADASTRO (quando o XML veio na unidade do estoque e o cadastro compra numa embalagem com
 * varias dela — a mesma condicao da linha nova), depois a linha IRMA (o mesmo item noutro CNPJ da
 * empresa) com Qtd > 1. `null` = fica como esta.
 *
 * ⚠️ So troca de EMBALAGEM (UN -> PCT). A mesma sigla com outra Qtd ("CX / 1" -> "CX / 100") fica de
 * fora: e' a linha legada que a [DA-865] deixou pro acerto na tela, e o preco dela depende do
 * desempate pelo preco atual — trocar a Qtd sozinha mudaria o preco 100x sem ninguem mexer.
 */
export function embalagemAdotadaPelaLinha(e: EmbalagemPelaNotaEntrada & {
  /** O que a linha desta filial tem hoje (vazio = linha nova). */
  embalagemLinha: string | null | undefined;
  qtdLinha: number | string | null | undefined;
  /** A linha do mesmo item noutro CNPJ da empresa, se houver. */
  irma?: { embalagem: string | null | undefined; quantidade: number | string | null | undefined } | null;
}): { embalagem: string; quantidade: number; origem: 'cadastro' | 'irma' } | null {
  if (numero(e.qtdLinha) > 1) return null;
  const atual = sigla(e.embalagemLinha);
  const doCadastro = embalagemDaLinhaNovaPelaNota(e);
  let cand: { embalagem: string; quantidade: number; origem: 'cadastro' | 'irma' } | null = null;
  if (doCadastro.doCadastro && doCadastro.quantidade > 1) {
    cand = { embalagem: doCadastro.embalagem, quantidade: doCadastro.quantidade, origem: 'cadastro' };
  } else if (e.irma && sigla(e.irma.embalagem) && numero(e.irma.quantidade) > 1) {
    cand = { embalagem: sigla(e.irma.embalagem), quantidade: numero(e.irma.quantidade), origem: 'irma' };
  }
  if (!cand || (atual && cand.embalagem === atual)) return null;
  return cand;
}

/**
 * O preco DA EMBALAGEM da linha do catalogo, a partir do preco da nota.
 *
 * O XML da o preco da unidade comercial dele; dividido pelo Qt.Emb do item vira o preco da unidade
 * do estoque; multiplicado pelo `estoqueNaEmbalagem` (`estoqueNaEmbalagemDaLinha`) vira o preco da
 * embalagem da linha. Ex.: torneira 1,241 por UN, linha PCT 12 (12 do estoque) -> 14,892; parafuso
 * 19,638 a CX, linha CX / 100 (1 do estoque) -> 19,638.
 *
 * 5 casas, a escala de `fornecedor_catalogo.preco_unitario`.
 */
export function precoDaEmbalagemPelaNota(
  precoXml: number | string | null | undefined,
  qtdEmbItem: number | string | null | undefined,
  estoqueNaEmbalagem: number | null | undefined,
): number | null {
  const preco = numero(precoXml);
  if (!(preco > 0)) return null;
  const qtdItem = numero(qtdEmbItem) > 0 ? numero(qtdEmbItem) : 1;
  const s = estoqueNaEmbalagem != null && estoqueNaEmbalagem > 0 ? estoqueNaEmbalagem : qtdItem;
  return Math.round((preco / qtdItem) * s * 1e5) / 1e5;
}
